import { motion } from "framer-motion";
import { MessageSquare, Share2, ThumbsDown, ThumbsUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../components/BackButton";
import api from "../services/api";

const IssuesPage = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: "", district: "", category: "", status: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/issues")
      .then((response) => setIssues(response.data || []))
      .catch((error) => console.error("Error fetching issues", error))
      .finally(() => setLoading(false));
  }, []);

  const filteredIssues = issues.filter(
    (issue) =>
      (!filters.state || issue.state === filters.state) &&
      (!filters.district || issue.district === filters.district) &&
      (!filters.category || issue.category === filters.category) &&
      (!filters.status || issue.status === filters.status),
  );

  const uniqueValues = (key) => [...new Set(issues.map((issue) => issue[key]).filter(Boolean))];

  const statusColorMap = {
    Resolved: "bg-green-100 text-green-800",
    Pending: "bg-yellow-100 text-yellow-800",
    Escalated: "bg-red-100 text-red-800",
    "Under Review": "bg-orange-100 text-orange-800",
  };

  return (
    <div className="bg-gradient-to-b from-orange-50 via-white to-green-50 min-h-screen py-10 px-4">
      <div className="max-w-7xl mx-auto">
        <BackButton className="mb-6" />
        <h1 className="text-4xl font-bold text-center text-orange-700 mb-4 tracking-wide">
          Public Issues Across Bharat
        </h1>
        <p className="text-center text-gray-600 mb-10">
          Explore, filter, and follow the issues citizens are raising right now.
        </p>

        <div className="text-center mb-8">
          <button
            onClick={() => navigate("/raise-issue")}
            className="rounded-full bg-gradient-to-r from-orange-500 to-green-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:scale-[1.02]"
          >
            Raise a New Issue
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 mb-10">
          {["state", "district", "category", "status"].map((field) => (
            <select
              key={field}
              value={filters[field]}
              onChange={(event) =>
                setFilters((current) => ({ ...current, [field]: event.target.value }))
              }
              className="rounded-2xl border border-green-100 bg-white/80 px-4 py-3 text-sm shadow-sm outline-none focus:border-orange-300"
            >
              <option value="">{field[0].toUpperCase() + field.slice(1)}</option>
              {uniqueValues(field).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-green-700 font-semibold">Loading issues...</div>
        ) : filteredIssues.length === 0 ? (
          <div className="py-20 text-center text-gray-500">No issues found for the selected filters.</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredIssues.map((issue, index) => (
              <motion.button
                key={issue._id || index}
                type="button"
                whileHover={{ scale: 1.02 }}
                onClick={() => navigate(`/issues/${issue._id}`)}
                className="rounded-3xl border border-gray-200 bg-white p-5 text-left shadow-md transition hover:shadow-xl"
              >
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">{issue.title}</h3>
                <p className="mt-2 line-clamp-3 text-sm text-gray-600">{issue.description}</p>

                <div className="mt-4 flex items-center justify-between text-xs">
                  <span
                    className={`rounded-full px-3 py-1 font-semibold ${
                      statusColorMap[issue.status] || "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {issue.status || "Unknown"}
                  </span>
                  <span className="text-gray-500">
                    {issue.state || "State"}, {issue.district || "District"}
                  </span>
                </div>

                <div className="mt-5 flex items-center justify-between text-gray-400">
                  <div className="flex gap-4">
                    <ThumbsUp className="h-4 w-4" />
                    <ThumbsDown className="h-4 w-4" />
                  </div>
                  <div className="flex gap-4">
                    <MessageSquare className="h-4 w-4" />
                    <Share2 className="h-4 w-4" />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssuesPage;
